import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
type EvaluationCategory = {
  heading: string;
  body: string;
  items: string[];
  linkLabel: string;
};
const evaluationCategories: EvaluationCategory[] = [{
  heading: 'Healthcare Data Use Cases',
  body: 'We evaluate whether solutions can support the data populations and operating structures your organization manages.',
  items: ['Employed, affiliated, referring, ordering, and rendering providers', 'Residents, fellows, and Graduate Medical Education populations', 'Acute, ambulatory, rural, and federally qualified care settings', 'Health Information Exchanges and Clinically Integrated Networks', 'Provider directories aligned to national standards'],
  linkLabel: 'Discuss These Use Cases'
}, {
  heading: 'Enterprise Data Capabilities',
  body: 'We assess whether a solution can support the domains and controls required for enterprise Data & Analytics, AI, interoperability, and operational use.',
  items: ['Provider', 'Patient and Member', 'Organization and Location', 'Plan and Coverage', 'Reference Data', 'Multi-domain mastering', 'Matching and survivorship', 'Stewardship, lineage, data quality, and lifecycle management'],
  linkLabel: 'Review Capability Needs'
}, {
  heading: 'Architecture and Deployment',
  body: 'The right solution must also fit the organization’s architecture, security, operating model, and regulatory obligations.',
  items: ['Centralized, federated, registry, and coexistence MDM', 'Cloud, on-premises, and hybrid deployment', 'FedRAMP and government requirements', 'FHIR, ONC, and CMS alignment', 'Integration with EHR, analytics, directory, and enterprise platforms', 'Long-term scalability and total cost considerations'],
  linkLabel: 'Discuss Architecture Options'
}];
const availableTo = ['Legally established healthcare organizations', 'Health systems and provider organizations', 'Academic medical centers', 'Health plans and healthcare networks', 'Government healthcare organizations', 'Nonprofit organizations supporting healthcare delivery or interoperability'];
const restrictions = ['Not available for personal use', 'Not available to analyst organizations', 'Not available for competitive market research', 'Not available for redistribution', 'Requests are reviewed before the resource is released'];
export function HealthcareTechnologySolutionsPage() {
  return <div className="min-h-screen bg-black" data-id="element-260">
      <Header data-id="element-261" />
      <main className="px-6 pt-32 pb-24" data-id="element-262">
        <div className="mx-auto max-w-7xl" data-id="element-263">
          <section className="max-w-4xl pb-20 pt-4 md:pb-24" data-id="element-264">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.16em] text-white/60" data-id="element-265">
              Healthcare Technology Solutions
            </p>
            <h1 className="max-w-3xl text-4xl font-medium leading-tight text-white md:text-5xl lg:text-6xl" data-id="element-266">
              Not Every MDM Platform Is Built for Healthcare
            </h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-white/80 md:text-xl" data-id="element-267">
              Vantage Data Group evaluates Master Data Management and Data
              Governance technologies against healthcare operating models, data
              domains, interoperability standards, regulatory requirements, and
              deployment needs.
            </p>
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white/70 md:text-xl" data-id="element-268">
              Some platforms perform well in general analyst evaluations but do
              not support the provider, organization, location, patient, member,
              and reference data capabilities healthcare organizations now
              require.
            </p>
            <div className="mt-9 flex flex-col items-start gap-5 sm:flex-row sm:items-center" data-id="element-269">
              <Link to="/contact" className="inline-flex h-12 items-center justify-center rounded-xl bg-white px-6 text-sm font-semibold text-black transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-270">
                Discuss Your Use Case
              </Link>
              <Link to="/contact" className="text-sm font-semibold text-white/75 underline decoration-white/35 underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-black" data-id="element-271">
                Request the Healthcare MDM Capability Matrix
              </Link>
            </div>
          </section>

          <section className="border-t border-white/10 py-16 md:py-20" data-id="element-272">
            <div className="mb-9 max-w-3xl" data-id="element-273">
              <h2 className="text-3xl font-semibold text-white md:text-4xl" data-id="element-274">
                Evaluate What Healthcare Requires
              </h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-3" data-id="element-275">
              {evaluationCategories.map(category => <article key={category.heading} className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6 lg:p-8" data-id="element-276">
                  <h3 className="text-2xl font-semibold text-white" data-id="element-277">
                    {category.heading}
                  </h3>
                  <p className="mt-4 text-white/70 leading-relaxed" data-id="element-278">
                    {category.body}
                  </p>
                  <ul className="mt-6 grid gap-x-5 gap-y-3 text-sm leading-relaxed text-white/70 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-id="element-279">
                    {category.items.map(item => <li key={item} className="border-l border-white/20 pl-3" data-id="element-280">
                        {item}
                      </li>)}
                  </ul>
                  <Link to="/contact" className="mt-8 text-sm font-semibold text-white underline decoration-white/35 underline-offset-4 transition-colors hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-black" data-id="element-281">
                    {category.linkLabel}
                  </Link>
                </article>)}
            </div>
          </section>

          <section className="border-y border-white/10 bg-white/5 px-6 py-16 md:px-12 md:py-20" data-id="element-282">
            <div className="mx-auto max-w-4xl" data-id="element-283">
              <h2 className="max-w-3xl text-3xl font-semibold text-white md:text-4xl" data-id="element-284">
                Start with Healthcare Requirements, Not Vendor Rankings
              </h2>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/80" data-id="element-285">
                Vantage Data Group maintains a curated view of technologies that
                may support healthcare-specific Master Data Management and Data
                Governance requirements.
              </p>
              <p className="mt-4 max-w-3xl text-lg leading-relaxed text-white/70" data-id="element-286">
                The resource helps qualified organizations narrow the market
                based on their data domains, operating model, architecture,
                interoperability needs, and future-state requirements.
              </p>
              <Link to="/contact" className="mt-8 inline-flex h-12 items-center justify-center rounded-xl bg-white px-6 text-sm font-semibold text-black transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-287">
                Request the Free Capability Matrix
              </Link>
            </div>
          </section>

          <section className="py-16 md:py-20" data-id="element-288">
            <div className="grid gap-10 border-b border-white/10 pb-10 md:grid-cols-2 md:gap-16" data-id="element-289">
              <div data-id="element-290">
                <h2 className="text-2xl font-semibold text-white" data-id="element-291">
                  Available To
                </h2>
                <ul className="mt-6 space-y-3 text-white/70 leading-relaxed" data-id="element-292">
                  {availableTo.map(item => <li key={item} className="border-l border-white/20 pl-3" data-id="element-293">
                      {item}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-294">
                <h2 className="text-2xl font-semibold text-white" data-id="element-295">
                  Resource Restrictions
                </h2>
                <ul className="mt-6 space-y-3 text-white/70 leading-relaxed" data-id="element-296">
                  {restrictions.map(item => <li key={item} className="border-l border-white/20 pl-3" data-id="element-297">
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>
            <p className="mt-8 max-w-5xl text-sm leading-relaxed text-white/55" data-id="element-298">
              The Healthcare MDM Capability Matrix is an educational starting
              point and does not represent a universal ranking, endorsement, or
              guarantee of product fit. Technology recommendations require
              review of each organization’s business requirements, data domains,
              architecture, governance maturity, security obligations,
              interoperability strategy, and budget.
            </p>
          </section>

          <section className="border-t border-white/10 pt-16 text-left md:pt-20" data-id="element-299">
            <div className="max-w-4xl" data-id="element-300">
              <h2 className="text-3xl font-semibold text-white md:text-4xl" data-id="element-301">
                Unsure Which Capabilities Your Organization Needs?
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-white/80" data-id="element-302">
                Before comparing platforms, understand the provider populations,
                data domains, workflows, standards, deployment model, and future
                requirements the technology must support.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-white/70" data-id="element-303">
                Vantage Data Group can help your organization define those
                requirements and narrow the market before an RFP, platform
                purchase, or implementation begins.
              </p>
              <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row" data-id="element-304">
                <Link to="/contact" className="inline-flex h-12 items-center justify-center rounded-xl bg-white px-6 text-sm font-semibold text-black transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-305">
                  Schedule an Advisory Discussion
                </Link>
                <Link to="/contact" className="inline-flex h-12 items-center justify-center rounded-xl border-2 border-white/60 px-6 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-306">
                  Request the Capability Matrix
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer data-id="element-307" />
    </div>;
}